import { useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified API hook that works for admin, atelier, and mechanic roles.
 * - mode='admin' or 'atelier': uses /api/saas prefix
 * - mode='mechanic': uses /api/mechanic prefix
 * - atelierOrgId: optional sub-org ID for atelier fleet operations (appended as query param)
 */
export function useUnifiedApi(token, mode = 'admin', atelierOrgId = null) {
  const prefix = mode === 'mechanic' ? '/api/mechanic' : '/api/saas';

  const appendOrgId = useCallback((path) => {
    if (!atelierOrgId) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}atelier_org_id=${atelierOrgId}`;
  }, [atelierOrgId]);

  const get = useCallback(async (path) => {
    try {
      const response = await axios.get(`${API_URL}${prefix}${appendOrgId(path)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur' };
    }
  }, [token, prefix, appendOrgId]);

  const post = useCallback(async (path, body) => {
    try {
      const response = await axios.post(`${API_URL}${prefix}${appendOrgId(path)}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur' };
    }
  }, [token, prefix, appendOrgId]);

  const put = useCallback(async (path, body) => {
    try {
      const response = await axios.put(`${API_URL}${prefix}${appendOrgId(path)}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur' };
    }
  }, [token, prefix, appendOrgId]);

  const patch = useCallback(async (path, body) => {
    try {
      const response = await axios.patch(`${API_URL}${prefix}${appendOrgId(path)}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur' };
    }
  }, [token, prefix, appendOrgId]);

  const del = useCallback(async (path) => {
    try {
      const response = await axios.delete(`${API_URL}${prefix}${appendOrgId(path)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Erreur' };
    }
  }, [token, prefix, appendOrgId]);

  return { get, post, put, patch, del, mode, token, atelierOrgId };
}

/**
 * Returns route paths based on the current mode.
 * Ensures navigation targets are correct for admin vs mechanic.
 */
export function useRoutePaths(mode) {
  const base = mode === 'mechanic' ? '/admin/mecanicien' : '/admin';
  return {
    fleet: `${base}/${mode === 'mechanic' ? 'flotte' : 'fleet'}`,
    fleetAtelier: '/admin/fleet-atelier',
    inspection: (vehicleId) => mode === 'mechanic'
      ? `/admin/mecanicien/inspections?vehicle=${vehicleId}`
      : `/admin/fleet/${vehicleId}/inspection`,
    measurements: (vehicleId) => mode === 'mechanic'
      ? `/admin/mecanicien/maintenance?vehicle=${vehicleId}`
      : `/admin/fleet/${vehicleId}/measurements`,
    workOrders: (vehicleId) => mode === 'mechanic'
      ? `/admin/mecanicien/bons?vehicle=${vehicleId}`
      : `/admin/atelier?vehicle=${vehicleId}`,
    maintenance: (vehicleId) => `${base}/maintenance?vehicle=${vehicleId}`,
    maintenancePage: `${base}/maintenance`,
    connections: mode === 'mechanic' ? '/admin/mecanicien/connexions' : '/admin/connections',
  };
}
